import { mdsPanelWidths, mdsSidebarWidths } from "@/design-system/foundations";
import localDb from "@/domains/local-db";
import {
  GoToMemItOptions,
  RelatedCard,
  SidePanelRoute,
  SidePanelRouteType,
  SidePanelState,
  SidePanelTab,
} from "@/store/routing/types";
import { AppSubStore, AppSubStoreArgs } from "@/store/types";
import { isEmpty, isEqual } from "lodash-es";
import {
  action,
  computed,
  makeObservable,
  observable,
  ObservableMap,
  reaction,
  runInAction,
} from "mobx";
import { matchPath } from "react-router";
import { convertPathToStringWithoutTid } from "@/store/navigation/helpers";
import { TabChangeHandler } from "@/store/navigation/types";
import { ScreenSize } from "@/design-system/foundations/types";

export class AppStoreSidePanelStore extends AppSubStore {
  sidePanelFocused: boolean;
  sidePanelState: SidePanelState;
  sidePanelSize = 0;
  private skipImplicitTabSelectionOnOpen = false;

  activeTabId: SidePanelTab;
  tabs: ObservableMap<SidePanelTab, SidePanelRoute[]>;
  private chatInactivityChecker?: () => boolean;

  constructor(injectedDeps: AppSubStoreArgs) {
    super(injectedDeps);

    this.sidePanelState = SidePanelState.SPLIT_VIEW;
    this.sidePanelFocused = false;

    this.activeTabId = SidePanelTab.MEM_IT;
    this.tabs = new ObservableMap<SidePanelTab, SidePanelRoute[]>();

    makeObservable<this, "chatInactivityChecker" | "skipImplicitTabSelectionOnOpen">(this, {
      // PERSISTENCE
      initialize: false,
      persistTabs: false,

      // SIDE PANEL STATE
      sidePanelSize: observable,
      sidePanelState: observable,
      skipImplicitTabSelectionOnOpen: observable,
      canRenderSplitView: computed,
      isMainPanelOpen: computed,
      isSidePanelOpen: computed,
      isSplitView: computed,
      sidePanelWidth: computed,

      // SIDE PANEL FOCUS
      sidePanelFocused: observable,
      setSidePanelFocused: action,

      // TABS
      activeTabId: observable,
      tabs: observable,
      isHeadsUpActive: computed,
      activeRoute: computed,
      activeTabStack: computed,
      defaultTabRoute: computed,

      // ACTIONS
      setSidePanelWidth: action,
      switchTab: action,
      navigateTo: action,
      goToChat: action,
      goToHeadsUp: action,
      goToMemIt: action,
      handleTabChange: action,
      goToCopilot: action,
      toggle: action,
      open: action,
      close: action,
      goUp: action,

      // INJECTED DEPENDENCIES
      setChatInactivityChecker: false,
      chatInactivityChecker: true,
    });

    reaction(
      () => this.sidePanelState,
      state => {
        this.store.memDb.settings.setSidePanelState(state);
      }
    );

    reaction(
      () => this.activeTabId,
      activeTab => {
        this.store.memDb.settings.setSidePanelActiveTabId(activeTab);
      }
    );
  }

  // SIDE PANEL STATE
  get isSplitView() {
    return this.sidePanelState === SidePanelState.SPLIT_VIEW;
  }

  get isMainPanelOpen() {
    if (!this.canRenderSplitView && this.isSplitView) {
      if (!this.sidePanelFocused) return true;
      else return false;
    } else {
      return this.sidePanelState === SidePanelState.MAIN_PANEL_ONLY || this.isSplitView;
    }
  }

  get isSidePanelOpen() {
    if (!this.canRenderSplitView && this.isSplitView) {
      if (this.sidePanelFocused) return true;
      else return false;
    } else {
      return this.sidePanelState === SidePanelState.SIDE_PANEL_ONLY || this.isSplitView;
    }
  }

  get canRenderSplitView() {
    if (!this.store.interface.dimensionsState.data) return true;
    const { min, gutter } = mdsPanelWidths();
    const { collapsed } = mdsSidebarWidths();
    return this.store.interface.dimensionsState.data?.width > min * 2 + gutter * 2 + collapsed;
  }

  get sidePanelWidth() {
    if (!this.isSidePanelOpen) return "auto";

    return this.sidePanelSize;
  }

  // SIDE PANEL FOCUS
  setSidePanelFocused = (focused: boolean) => {
    this.sidePanelFocused = focused;
  };

  // TABS
  get isHeadsUpActive() {
    return this.activeRoute.type === SidePanelRouteType.HEADS_UP;
  }

  get activeRoute(): SidePanelRoute {
    return this.activeTabStack[this.activeTabStack.length - 1] || this.defaultTabRoute;
  }

  get activeTabStack(): SidePanelRoute[] {
    return this.tabs.get(this.activeTabId) || [this.defaultTabRoute];
  }

  get defaultTabRoute(): SidePanelRoute {
    return {
      type: SidePanelRouteType.MEM_IT,
    };
  }

  // ACTIONS
  setSidePanelWidth = (width: number) => {
    this.sidePanelSize = width;
  };

  switchTab = (tabId: SidePanelTab) => {
    this.activeTabId = tabId;
  };

  navigateTo = (
    route: SidePanelRoute,
    { resetStack = false, persist = true }: { resetStack?: boolean; persist?: boolean } = {}
  ) => {
    const activeStack = this.tabs.get(this.activeTabId);

    if (!activeStack || resetStack) {
      this.tabs.set(this.activeTabId, [route]);
    } else {
      // push the route unless it's already the top of the stack
      const lastRoute = activeStack[activeStack.length - 1];
      if (!lastRoute || !isEqual(lastRoute, route)) {
        activeStack.push(route);
      }
    }

    // Only persist if explicitly requested (defaults to true for backward compatibility)
    if (persist) {
      this.persistTabs();
    }
  };

  // Deprecated: TODO: Remove this when merging in Heads Up
  goToCopilot = (_card?: RelatedCard) => {
    this.switchTab(SidePanelTab.HEADS_UP);
    this.navigateTo({ type: SidePanelRouteType.HEADS_UP }, { resetStack: true });
    this.open();
  };

  goToChat = async (_conversationId?: string) => {
    // Deprecated: route Chat requests to MemIt
    this.switchTab(SidePanelTab.MEM_IT);
    this.navigateTo({ type: SidePanelRouteType.MEM_IT }, { resetStack: true });
    this.skipImplicitTabSelectionOnOpen = true;
    this.open();
  };

  goToHeadsUp = () => {
    this.switchTab(SidePanelTab.HEADS_UP);
    this.navigateTo({ type: SidePanelRouteType.HEADS_UP }, { resetStack: true, persist: false });
    this.skipImplicitTabSelectionOnOpen = true;
    this.open();
  };

  goToMemIt = (sessionId?: string, { openChatPanel = true }: GoToMemItOptions = {}) => {
    if (openChatPanel) {
      this.switchTab(SidePanelTab.MEM_IT);
    }

    this.navigateTo(
      { type: SidePanelRouteType.MEM_IT, sessionId },
      { resetStack: true, persist: false }
    );

    if (openChatPanel) {
      this.skipImplicitTabSelectionOnOpen = true;
      this.open();
    }
  };

  handleTabChange: TabChangeHandler = ({
    path,
    openInBackground,
    isNew,
    suppressSidePanelRouteHandler,
  }) => {
    if (suppressSidePanelRouteHandler) return;

    const resolvedPath = convertPathToStringWithoutTid(path);
    const sidePanelIsOpen = this.isSidePanelOpen;
    const isNoteTab = !openInBackground && !!matchPath("/notes/:noteId", resolvedPath);
    const chatIsInactive = this.chatInactivityChecker?.() ?? true;
    const isDesktopSize = this.store.interface.screenSizeState.data === ScreenSize.Desktop;

    // changing tabs on mobile closes the side panel
    if (!isDesktopSize && sidePanelIsOpen) {
      this.close();
      return;
    }

    // New note tabs open Heads Up if the side panel is closed, if wide enough
    if (isDesktopSize && !sidePanelIsOpen && isNoteTab && isNew) {
      this.open();
      this.switchTab(SidePanelTab.HEADS_UP);
      return;
    }

    // Note tabs switch to Heads Up if the side panel is open and the chat is inactive
    if (sidePanelIsOpen && isNoteTab && chatIsInactive) {
      this.switchTab(SidePanelTab.HEADS_UP);
      return;
    }

    // Non-Note tabs switch to Mem It if the side panel is open
    if (sidePanelIsOpen && !isNoteTab) {
      this.switchTab(SidePanelTab.MEM_IT);
      return;
    }
  };

  setChatInactivityChecker = (fn: () => boolean) => {
    this.chatInactivityChecker = fn;
  };

  goUp = () => {
    const activeStack = this.tabs.get(this.activeTabId);
    if (activeStack && activeStack.length > 1) {
      activeStack.pop();
    } else {
      this.close();
    }
    this.persistTabs();
  };

  toggle = () => {
    if (this.isSplitView) this.close();
    else this.open();
  };

  open = () => {
    const noSplitView = !this.canRenderSplitView;
    if (noSplitView) this.sidePanelFocused = true;

    // If we can't do split view, when opening the side panel without an explicit tab selection,
    // choose Heads Up only if on a note page AND chat is inactive.
    if (noSplitView && !this.skipImplicitTabSelectionOnOpen) {
      const chatIsInactive = this.chatInactivityChecker?.() ?? true;
      const onNote = this.store.navigation?.isCurrentlyOnNotePage;
      if (onNote && chatIsInactive) this.switchTab(SidePanelTab.HEADS_UP);
    }

    this.sidePanelState = SidePanelState.SPLIT_VIEW;
    this.skipImplicitTabSelectionOnOpen = false;
  };

  close = () => {
    this.sidePanelFocused = false;
    this.sidePanelState = SidePanelState.MAIN_PANEL_ONLY;
  };

  initialize = async () => {
    const [res, memDbState] = await Promise.all([
      localDb.settings.getSidePanelState(),
      this.store.memDb.settings.getSidePanelState(),
    ]);

    if (!isEmpty(res)) {
      if (res.sidePanelState) {
        await this.store.memDb.settings.setSidePanelState(res.sidePanelState);
        await localDb.settings.removeSidePanelState();
      }
      if (res.activeTabId) {
        await this.store.memDb.settings.setSidePanelActiveTabId(res.activeTabId);
        await localDb.settings.removeSidePanelActiveTabId();
      }
      if (res.tabs) {
        await this.store.memDb.settings.setSidePanelTabs(Array.from(res.tabs.entries()));
        await localDb.settings.removeSidePanelTabs();
      }
      runInAction(() => {
        if (res.activeTabId) this.activeTabId = res.activeTabId;
        if (res.sidePanelState) this.sidePanelState = res.sidePanelState;
        if (res.tabs) this.tabs.replace(res.tabs);
      });
      return;
    }

    if (!isEmpty(memDbState)) {
      runInAction(() => {
        if (memDbState.activeTabId) this.activeTabId = memDbState.activeTabId;
        if (memDbState.sidePanelState) this.sidePanelState = memDbState.sidePanelState;
        if (memDbState.tabs) this.tabs.replace(memDbState.tabs);
      });
    }

    // experimental (as of Sep 10, 2025)
    runInAction(() => {
      const isDesktopBreakpoint = this.store.interface.screenSizeState.data === ScreenSize.Desktop;
      if (!isDesktopBreakpoint) return;

      // We're always opening the split view when loading.
      // When we're loading into a note tab, open Heads Up.
      // Otherwise, open Mem It.
      // We're not 100% committed to that, so we're keeping the
      // restoration logic in place for now with this override for
      // the time being.
      this.sidePanelState = SidePanelState.SPLIT_VIEW;
      if (this.store.navigation?.isCurrentlyOnNotePage) {
        this.switchTab(SidePanelTab.HEADS_UP);
      } else {
        this.switchTab(SidePanelTab.MEM_IT);
      }
    });
  };

  persistTabs = () => {
    this.store.memDb.settings.setSidePanelTabs(this.tabs.toJSON());
  };
}
